import React, { useCallback, useState, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { uploadFile, validateFile } from '../../utils/fileUpload'
import { 
  Upload, 
  X, 
  Image as ImageIcon, 
  FileImage, 
  AlertCircle,
  Check,
  Loader
} from 'lucide-react'

const FileUpload = ({ 
  onFileSelect, 
  currentImageUrl = '', 
  accept = { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
  maxSize = 5 * 1024 * 1024, // 5MB
  className = '' 
}) => {
  const [preview, setPreview] = useState(currentImageUrl)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [error, setError] = useState('')
  const [isUploading, setIsUploading] = useState(false)

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    setError('')
    
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0]
      if (rejection.errors[0].code === 'file-too-large') {
        setError(`File is too large. Maximum size is ${maxSize / (1024 * 1024)}MB`)
      } else if (rejection.errors[0].code === 'file-invalid-type') {
        setError('Only image files (JPEG, PNG, WebP) are allowed')
      } else {
        setError(rejection.errors[0].message)
      }
      return
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      
      try {
        // Validate file
        validateFile(file)
        
        setIsUploading(true)
        
        // Create preview URL for the file
        const previewUrl = URL.createObjectURL(file)
        
        // Set preview and uploaded file
        setPreview(previewUrl)
        setUploadedFile(file)
        
        // Call the parent callback with the actual File object
        // This is the actual file that will be sent to the backend via FormData
        onFileSelect(file)
        
      } catch (uploadError) {
        setError(uploadError.message || 'Failed to upload file')
      } finally {
        setIsUploading(false)
      }
    }
  }, [maxSize, onFileSelect])

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple: false,
    disabled: isUploading
  })

  const removeImage = () => {
    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview)
    }
    setPreview('')
    setUploadedFile(null)
    setError('')
    onFileSelect(null)
  }

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview)
      }
    }
  }, [preview])

  // Update preview when currentImageUrl changes
  useEffect(() => {
    if (currentImageUrl && currentImageUrl !== preview) {
      setPreview(currentImageUrl)
    }
  }, [currentImageUrl])

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Dropzone Area */}
      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200
          ${isUploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
          ${isDragActive && !isUploading ? 'border-primary-purple bg-purple-50' : 'border-gray-300 hover:border-primary-purple hover:bg-gray-50'}
          ${isDragReject ? 'border-red-400 bg-red-50' : ''}
          ${error ? 'border-red-400 bg-red-50' : ''}
        `}
      >
        <input {...getInputProps()} />
        
        {preview ? (
          // Image Preview
          <div className="relative group">
            <img
              src={preview}
              alt="Preview"
              className="max-w-full max-h-48 mx-auto rounded-lg shadow-sm"
            />
            {isUploading ? (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                <div className="text-white text-center">
                  <Loader className="w-8 h-8 mx-auto mb-2 animate-spin" />
                  <p className="text-sm">Uploading...</p>
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex items-center justify-center">
                <div className="text-white text-center">
                  <Upload className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">Click or drag to replace</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Upload Prompt
          <div className="space-y-3">
            <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
              {isDragActive ? (
                <Upload className="w-6 h-6 text-primary-purple" />
              ) : (
                <ImageIcon className="w-6 h-6 text-gray-400" />
              )}
            </div>
            
            <div>
              <p className="text-lg font-medium text-gray-900">
                {isDragActive ? 'Drop the image here' : 'Upload image'}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Drag & drop an image here, or click to select
              </p>
            </div>
            
            <div className="text-xs text-gray-500">
              <p>Supports: JPEG, PNG, WebP</p>
              <p>Max size: {maxSize / (1024 * 1024)}MB</p>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* File Info */}
      {uploadedFile && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 rounded p-1">
                <FileImage className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-green-900">{uploadedFile.name}</p>
                <p className="text-xs text-green-600">
                  {(uploadedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  removeImage()
                }}
                className="text-green-600 hover:text-green-800 p-1"
                title="Remove image"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Image Info (when editing) */}
      {currentImageUrl && !uploadedFile && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 rounded p-1">
                <ImageIcon className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-blue-900">Current Image</p>
                <p className="text-xs text-blue-600 truncate max-w-48">
                  {currentImageUrl}
                </p>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation()
                removeImage()
              }}
              className="text-blue-600 hover:text-blue-800 p-1"
              title="Remove current image"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default FileUpload